import {
	getApiUrl,
	OrderDirection,
	type PartnerFilterDto,
	type PartnerGeneralResponseDto,
	PartnerStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { PartnerPaths } from "Partner/Routes/Routing";
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_LIMIT,
	useTableFilter,
} from "Shared/Hooks/useTableFilter";
import { Edit, Visibility } from "@mui/icons-material";
import {
	Chip,
	type ChipProps,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { createMRTColumnHelper } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router";

type TableAction = {
	label: string;
	action: () => void;
};

type PartnersListTableRow = PartnerGeneralResponseDto & {
	marketName?: string;
	marketIconPath?: string;
	actions: TableAction[];
};

const partnerStatusChipColor: Record<PartnerStatus, ChipProps["color"]> = {
	[PartnerStatus.ACTIVE]: "success",
	[PartnerStatus.DELETED]: "error",
	[PartnerStatus.BLOCKED]: "error",
};

const {
	detail: { generalInfo: partnerListTableResource },
	shared: partnerSharedResource,
	selectOptions: selectOptionsResource,
} = Resources.base.partner;

export const usePartnersListTable = (filter: PartnerFilterDto) => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<PartnersListTableRow>();
	const navigate = useNavigate();

	const { pageIndex, pageLimit, sorting, setSearchParams } = useTableFilter(
		DEFAULT_PAGE_LIMIT,
		DEFAULT_PAGE_INDEX,
		[{ id: "id", desc: true }],
	);
	const { data: partners, isLoading } = usePartnersListQuery({
		...filter,
		pageIndex,
		pageLimit,
		orderBy: sorting[0]?.id,
		orderDirection: sorting[0]?.desc ? OrderDirection.desc : OrderDirection.asc,
	});

	const handleRedirectToDetail = (partnerId: number) => {
		navigate(PartnerPaths.Detail.path(partnerId));
	};

	const handleRedirectToEdit = (partnerId: number) => {
		navigate(PartnerPaths.Edit.path(partnerId));
	};

	const tableData: PartnersListTableRow[] =
		useMemo(
			() =>
				(partners?.data ?? []).map((partner) => {
					return {
						...partner.partner,
						marketName: partner?.market?.name ?? undefined,
						marketIconPath: partner?.market?.iconPath
							? `${getApiUrl()}${partner.market.iconPath}`
							: undefined,
						actions: [],
					};
				}),
			[partners],
		) ?? [];

	const columns = [
		columnHelper.accessor((row) => row.id, {
			id: "id",
			header: t(partnerListTableResource.id),
			maxSize: 30,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.id}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.name, {
			id: "name",
			header: t(partnerListTableResource.name),
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{`${row.original.name} ${row.original.middleName && `${row.original.middleName} `}${row.original.surname}`}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.email, {
			id: "email",
			header: t(partnerListTableResource.email),
			maxSize: 100,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.email}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.phone, {
			id: "phone",
			header: t(partnerListTableResource.phone),
			maxSize: 100,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.phone}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.marketName, {
			id: "marketName",
			header: t(partnerListTableResource.marketName),
			maxSize: 80,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Stack direction="row" spacing={1} alignItems="center">
					<img
						src={row.original.marketIconPath}
						alt="country flag"
						width={15}
						height={15}
					/>
					<Typography variant="tableCell">{row.original.marketName}</Typography>
				</Stack>
			),
		}),
		columnHelper.accessor((row) => row.teamSize, {
			id: "teamSize",
			header: t(partnerListTableResource.teamSize),
			maxSize: 100,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.teamSize}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.isCanTrainDay, {
			id: "isCanTrainDay",
			header: t(partnerListTableResource.isCanTrainDay),
			maxSize: 100,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.isCanTrainDay}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.status, {
			id: "status",
			header: t(partnerListTableResource.status),
			maxSize: 60,
			enableColumnActions: false,
			Cell: ({ row }) => (
				<Chip
					size="small"
					variant="outlined"
					label={t(selectOptionsResource.status?.[row.original.status])}
					color={partnerStatusChipColor[row.original.status]}
				/>
			),
		}),
		columnHelper.accessor("actions", {
			id: "actions",
			header: t(partnerSharedResource.action),
			maxSize: 60,
			enableColumnActions: false,
			enableSorting: false,
			Cell: ({ row }) => (
				<Stack direction="row" columnGap={1}>
					<IconButton
						size="small"
						onClick={() => handleRedirectToDetail(row.original.id)}
						sx={{ background: "none" }}
					>
						<Visibility />
					</IconButton>
					<IconButton
						size="small"
						onClick={() => handleRedirectToEdit(row.original.id)}
						sx={{ background: "none" }}
					>
						<Edit />
					</IconButton>
				</Stack>
			),
		}),
	];

	const handlePaginationChange = (updater) => {
		const cleanedFilter = Object.fromEntries(
			Object.entries(filter).filter(([_, value]) => value !== undefined),
		);

		const newPagination = updater({ pageIndex, pageLimit });
		setSearchParams({ ...cleanedFilter, ...newPagination });
	};

	const handleSortingChange = (updater) => {
		const newSorting = updater(sorting);
		const cleanedFilter = Object.fromEntries(
			Object.entries(filter).filter(([_, value]) => value !== undefined),
		);

		setSearchParams({
			...cleanedFilter,
			sorting: newSorting,
			pageIndex,
			pageLimit,
		});
	};

	const tableOptions = {
		...defaultTableConfig,
		columns,
		initialState: {
			columnVisibility: {
				role: false,
				skills: false,
				teamSize: false,
				isCanTrainDay: false,
			},
		},
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
			pagination: {
				pageSize: pageLimit,
				pageIndex: pageIndex,
			},
			sorting: sorting,
		},
		enableColumnActions: false,
		enableTopToolbar: false,
		onSortingChange: handleSortingChange,
		manualSorting: true,
		muiPaginationProps: {
			showRowsPerPage: false,
			showFirstButton: false,
			showLastButton: false,
		},
		manualPagination: true,
		onPaginationChange: handlePaginationChange,
		rowCount: partners?.metaData?.count ?? 0,
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				const partnerId = row.original.id;
				navigate(PartnerPaths.Detail.path(partnerId));
			},
		}),
	};

	return tableOptions;
};
